import React, { useState, useEffect } from 'react';
import { configApi } from '../api';
import { Card, PageTitle, Input, Btn, Spinner } from '../components/common/UI';
import { Save, RotateCcw, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Config() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [form, setForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await configApi.get();
      setForm(data.config);
    } catch (_e) {
      toast.error('Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const deepSet = (obj, path, val) => {
    const keys = path.split('.');
    const next = Object.assign({}, obj);
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) {
      cur[keys[i]] = Object.assign({}, cur[keys[i]]);
      cur = cur[keys[i]];
    }
    cur[keys[keys.length - 1]] = val;
    return next;
  };

  const set = (path, val) => setForm((f) => deepSet(f, path, val));

  const save = async () => {
    setSaving(true);
    try {
      await configApi.update({
        messageInterval: parseInt(form.messageInterval, 10),
        rateLimit: {
          perMinute: parseInt(form.rateLimit.perMinute, 10),
          perHour: parseInt(form.rateLimit.perHour, 10),
          perDay: parseInt(form.rateLimit.perDay, 10),
        },
        safetySettings: {
          maxRetries: parseInt(form.safetySettings.maxRetries, 10),
          batchSize: parseInt(form.safetySettings.batchSize, 10),
          invalidNumberHandling: form.safetySettings.invalidNumberHandling,
        },
        alertEmail: form.alertEmail,
        notificationSettings: form.notificationSettings,
      });
      toast.success('Configuration saved!');
      await load();
    } catch (err) {
      const d = err.response && err.response.data;
      if (d && d.errors) {
        d.errors.forEach((e) => toast.error(e.field + ': ' + e.message));
      } else {
        toast.error('Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const validate = async () => {
    setValidating(true);
    try {
      await configApi.validate(form);
      toast.success('Configuration is valid ✓');
    } catch (err) {
      const d = err.response && err.response.data;
      toast.error((d && d.error) || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset to default configuration?')) return;
    try {
      const { data } = await configApi.reset();
      setForm(data.config);
      toast.success('Reset to defaults');
    } catch (_e) {
      toast.error('Reset failed');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spinner size={32} />
      </div>
    );
  }

  if (!form) return null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <PageTitle sub="Configure message delivery settings">Configuration</PageTitle>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="secondary" onClick={validate} disabled={validating}>
            {validating ? <Spinner size={14} /> : <CheckCircle size={14} />} Validate
          </Btn>
          <Btn variant="secondary" onClick={reset}><RotateCcw size={14} /> Reset</Btn>
          <Btn onClick={save} disabled={saving}>
            {saving ? <Spinner size={14} /> : <Save size={14} />} Save
          </Btn>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 }}>Message Delivery</h3>
          <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 8 }}>
            Message Interval (ms) — 1000 to 60000
          </label>
          <input
            type="range"
            min={1000}
            max={60000}
            step={500}
            value={form.messageInterval}
            onChange={(e) => set('messageInterval', e.target.value)}
            style={{ width: '100%', accentColor: '#25d366', marginBottom: 8 }}
          />
          <span style={{ color: '#25d366', fontSize: 14, fontWeight: 600 }}>
            {form.messageInterval}ms ({(form.messageInterval / 1000).toFixed(1)}s)
          </span>
        </Card>

        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 }}>Rate Limits</h3>
          <Input label="Per Minute (1–60)" type="number" min={1} max={60}
            value={form.rateLimit.perMinute}
            onChange={(e) => set('rateLimit.perMinute', e.target.value)} />
          <Input label="Per Hour (1–1000)" type="number" min={1} max={1000}
            value={form.rateLimit.perHour}
            onChange={(e) => set('rateLimit.perHour', e.target.value)} />
          <Input label="Per Day (1–10000)" type="number" min={1} max={10000}
            value={form.rateLimit.perDay}
            onChange={(e) => set('rateLimit.perDay', e.target.value)} />
        </Card>

        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 }}>Safety Settings</h3>
          <Input label="Max Retries (0–10)" type="number" min={0} max={10}
            value={form.safetySettings.maxRetries}
            onChange={(e) => set('safetySettings.maxRetries', e.target.value)} />
          <Input label="Batch Size (1–50)" type="number" min={1} max={50}
            value={form.safetySettings.batchSize}
            onChange={(e) => set('safetySettings.batchSize', e.target.value)} />
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, color: '#94a3b8', display: 'block', marginBottom: 6 }}>Invalid Number Handling</label>
            <select
              value={form.safetySettings.invalidNumberHandling}
              onChange={(e) => set('safetySettings.invalidNumberHandling', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 14, outline: 'none' }}
            >
              <option value="skip">Skip</option>
              <option value="fail">Fail</option>
              <option value="retry">Retry</option>
            </select>
          </div>
        </Card>

        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 }}>Notifications</h3>
          <Input label="Alert Email" type="email" value={form.alertEmail}
            onChange={(e) => set('alertEmail', e.target.value)} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
            {[['emailOnLogout', 'Email on Logout'], ['emailOnFailure', 'Email on Failure']].map((item) => (
              <label key={item[0]} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={(form.notificationSettings && form.notificationSettings[item[0]]) || false}
                  onChange={(e) => set('notificationSettings.' + item[0], e.target.checked)}
                  style={{ accentColor: '#25d366' }}
                />
                <span style={{ color: '#e2e8f0', fontSize: 13 }}>{item[1]}</span>
              </label>
            ))}
          </div>
        </Card>

        <Card style={{ gridColumn: '1 / -1' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 }}>Reconnect Config</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            <Input label="Max Attempts" type="number"
              value={(form.reconnectConfig && form.reconnectConfig.maxAttempts) || ''}
              onChange={(e) => set('reconnectConfig.maxAttempts', e.target.value)} />
            <Input label="Initial Delay (ms)" type="number"
              value={(form.reconnectConfig && form.reconnectConfig.initialDelay) || ''}
              onChange={(e) => set('reconnectConfig.initialDelay', e.target.value)} />
            <Input label="Max Delay (ms)" type="number"
              value={(form.reconnectConfig && form.reconnectConfig.maxDelay) || ''}
              onChange={(e) => set('reconnectConfig.maxDelay', e.target.value)} />
          </div>
        </Card>
      </div>
    </div>
  );
}
